import React from 'react';
import { Button, Space, Input, Typography, Select, InputNumber, Radio, Divider, Switch, Tooltip, Slider } from 'antd';
import { CLAUSE_COOLDOWN_MODE_OPTIONS } from './utils';

const ConfigurationForm = ({
  config,
  setConfig,
  handleStartDateChange,
  handleEndDateChange,
  applyEndPreset,
  handleRebalancingToggle,
  signalSource,
  handleSignalSourceChange,
  clauseDefinitionsLoading,
  loadClauseDefinitions,
  clauseDefinitions,
  formatClauseLabel,
  ensureClauseDataLoaded,
  ensureClauseSetsLoaded,
  setClauseManagerTab,
  setEditingClause,
  setClauseForm,
  setEditingClauseSet,
  setClauseCombinationForm,
  setClauseModalOpen,
  clauseSetsLoading,
  loadClauseSets,
  clauseSets,
  updateClauseCooldownDefault,
  selectedClauseRows,
  selectedClauseSetRows,
  renderClauseCooldownRow,
  renderClausePositionSizingRow,
  portfolioCashEur,
  selected,
  openDetails
}) => {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div>
        <Typography.Text strong>Start Portfolio</Typography.Text>
        <div>
          Selected: {selected ? (
            <>
              <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => openDetails(selected)}>
                {`#${selected.id} — ${selected.name}`}
              </Button>
              {/* DAS-1860: Show indicator for snapshot portfolios */}
              {selected.isSnapshot && (
                <span style={{ 
                  marginLeft: 8, 
                  padding: '2px 8px', 
                  background: '#e6f7ff', 
                  border: '1px solid #91d5ff',
                  borderRadius: 4,
                  fontSize: 11,
                  color: '#0050b3'
                }}>
                  Historical state from Run #{selected.snapshotRunId}
                </span>
              )}
            </>
          ) : config.start_portfolio_id ? (
            <Typography.Text type="secondary">
              #{config.start_portfolio_id} (not in local list - refresh portfolios)
            </Typography.Text>
          ) : 'None'}
        </div>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Period</Typography.Text>
        <Space>
          <div>
            <div style={{ fontSize: 12 }}>Start date</div>
            <Input type="date" value={config.start_date} onChange={handleStartDateChange} />
          </div>
          <div>
            <div style={{ fontSize: 12 }}>End date</div>
            <Space>
              <Input type="date" value={config.end_date} onChange={handleEndDateChange} />
              <Select
                placeholder="End presets"
                style={{ width: 160 }}
                onChange={(val) => applyEndPreset(val)}
                options={[
                  { label: '1 week', value: '1w' },
                  { label: '1 month', value: '1m' },
                  { label: '3 months', value: '3m' },
                  { label: '6 months', value: '6m' },
                  { label: '9 months', value: '9m' },
                  { label: '1 year', value: '1y' },
                  { label: '1.5 years', value: '1.5y' },
                  { label: '2 years', value: '2y' },
                ]}
              />
            </Space>
          </div>
        </Space>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Scenario Selection</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Typography.Text strong>Systematic Rebalancing (DAS-1643)</Typography.Text>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <div style={{ width: 220 }}>Enable rebalancing:</div>
                <Switch
                  checked={config.rebalancing.enabled}
                  onChange={handleRebalancingToggle}
                />
              </Space>
              {config.rebalancing.enabled && (
                <>
                  <Space>
                    <div style={{ width: 220 }}>Frequency (days):</div>
                    <InputNumber
                      min={1}
                      max={365}
                      value={config.rebalancing.frequency_days}
                      onChange={(val) => setConfig((c) => ({ ...c, rebalancing: { ...c.rebalancing, frequency_days: val === null ? c.rebalancing.frequency_days : val } }))}
                    />
                    <Typography.Text type="secondary">(63 = quarterly)</Typography.Text>
                  </Space>
                  <Space>
                    <div style={{ width: 220 }}>Selection percentile:</div>
                    <InputNumber
                      min={90}
                      max={99.9}
                      step={0.1}
                      value={config.rebalancing.selection_percentile}
                      onChange={(val) => setConfig((c) => ({ ...c, rebalancing: { ...c.rebalancing, selection_percentile: val ?? 98 } }))}
                    />
                    <Typography.Text type="secondary">(98 = top 2%, recommended for €200k)</Typography.Text>
                  </Space>
                  <Space>
                    <div style={{ width: 220 }}>Sector-neutral selection:</div>
                    <Switch
                      checked={config.rebalancing.sector_neutral}
                      onChange={(checked) => setConfig((c) => ({ ...c, rebalancing: { ...c.rebalancing, sector_neutral: checked } }))}
                    />
                  </Space>
                  
                  {/* DAS-1688: Max Sector Exposure (only when sector_neutral is OFF) */}
                  {!config.rebalancing.sector_neutral && (
                    <div style={{ marginTop: 8, padding: 12, background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span>🔒 Max Sector Exposure:</span>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>
                          {config.rebalancing.max_sector_exposure_pct === 100 ? 'No limit' : `${config.rebalancing.max_sector_exposure_pct || 40}%`}
                        </span>
                      </div>
                      <Slider
                        min={10}
                        max={100}
                        step={5}
                        value={config.rebalancing.max_sector_exposure_pct || 40}
                        onChange={(val) => setConfig((c) => ({
                          ...c,
                          rebalancing: { ...c.rebalancing, max_sector_exposure_pct: val }
                        }))}
                        marks={{
                          10: '10%',
                          40: '40%',
                          100: 'No limit'
                        }}
                      />
                      <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                        ℹ️ Limits any single sector to this percentage of your portfolio. Recommended: 30-50%.
                      </div>
                    </div>
                  )}
                  
                  {/* DAS-1764: Excluded Sectors (works with both sector_neutral ON and OFF) */}
                  {/* DAS-1775: Added 'Unknown' sector option for tickers with NULL sector data */}
                  <div style={{ marginTop: 8, padding: 12, background: '#fff1f0', borderRadius: 8, border: '1px solid #ffa39e' }}>
                    <Typography.Text strong>🚫 Exclude Sectors</Typography.Text>
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {['Energy', 'Utilities', 'Consumer Defensive', 'Healthcare', 'Real Estate', 'Basic Materials', 'Communication Services', 'Consumer Cyclical', 'Financial Services', 'Industrials', 'Technology', 'Unknown'].map(sector => (
                          <label key={sector} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '4px 8px', 
                            background: (config.rebalancing.excluded_sectors || []).includes(sector) ? '#ffccc7' : '#fff',
                            border: sector === 'Unknown' ? '1px dashed #faad14' : '1px solid #d9d9d9',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12
                          }}>
                            <input
                              type="checkbox"
                              checked={(config.rebalancing.excluded_sectors || []).includes(sector)}
                              onChange={(e) => {
                                const current = config.rebalancing.excluded_sectors || [];
                                const updated = e.target.checked 
                                  ? [...current, sector]
                                  : current.filter(s => s !== sector);
                                setConfig((c) => ({
                                  ...c,
                                  rebalancing: { ...c.rebalancing, excluded_sectors: updated }
                                }));
                              }}
                              style={{ marginRight: 4 }}
                            />
                            {sector === 'Unknown' ? '⚠️ Unknown' : sector}
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                        ℹ️ Exclude sectors that historically underperform with your factor strategy. Based on run 418 analysis: Energy, Utilities, Consumer Defensive, Real Estate are candidates.
                        <br/>⚠️ <strong>Unknown</strong>: Tickers with missing sector data (recommended to exclude).
                      </div>
                    </div>
                  
                  {/* DAS-1775: Market Cap Filter - Exclude tickers with unknown market cap */}
                  <div style={{ marginTop: 8, padding: 12, background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
                    <Space>
                      <Typography.Text strong>📊 Market Cap Filter</Typography.Text>
                      <Switch
                        size="small"
                        checked={config.rebalancing.exclude_unknown_market_cap ?? true}
                        onChange={(checked) => setConfig((c) => ({
                          ...c,
                          rebalancing: { ...c.rebalancing, exclude_unknown_market_cap: checked }
                        }))}
                      />
                      <span style={{ fontSize: 12, color: '#666' }}>
                        Exclude tickers with unknown market cap
                      </span>
                    </Space>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                      ℹ️ When enabled, tickers without market cap data in the database will be filtered out during selection.
                      This prevents trading illiquid or delisted stocks with missing fundamental data.
                    </div>
                  </div>
                  
                  {/* DAS-1762: Momentum Filter */}
                  <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                    <Space style={{ width: '100%' }} direction="vertical" size="small">
                      <Space>
                        <Typography.Text strong>🚀 Momentum Filter</Typography.Text>
                        <Switch
                          size="small"
                          checked={config.rebalancing.momentum_filter?.enabled ?? false}
                          onChange={(checked) => setConfig((c) => ({
                            ...c,
                            rebalancing: { 
                              ...c.rebalancing, 
                              momentum_filter: { 
                                ...c.rebalancing.momentum_filter,
                                enabled: checked 
                              }
                            }
                          }))}
                        />
                      </Space>
                      
                      {config.rebalancing.momentum_filter?.enabled && (
                        <div style={{ marginTop: 8 }}>
                          <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 150, fontSize: 12 }}>Min return (%):</span>
                              <InputNumber
                                size="small"
                                style={{ width: 80 }}
                                value={config.rebalancing.momentum_filter?.min_return_pct ?? 0}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  rebalancing: { 
                                    ...c.rebalancing, 
                                    momentum_filter: { 
                                      ...c.rebalancing.momentum_filter,
                                      min_return_pct: val ?? 0 
                                    }
                                  }
                                }))}
                                step={1}
                                min={-50}
                                max={100}
                              />
                              <span style={{ fontSize: 11, color: '#666' }}>
                                (0 = positive momentum only)
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 150, fontSize: 12 }}>Lookback (months):</span>
                              <InputNumber
                                size="small"
                                style={{ width: 80 }}
                                value={config.rebalancing.momentum_filter?.lookback_months ?? 12}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  rebalancing: { 
                                    ...c.rebalancing, 
                                    momentum_filter: { 
                                      ...c.rebalancing.momentum_filter,
                                      lookback_months: val ?? 12 
                                    }
                                  }
                                }))}
                                step={1}
                                min={1}
                                max={24}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 150, fontSize: 12 }}>Skip recent month:</span>
                              <Switch
                                size="small"
                                checked={config.rebalancing.momentum_filter?.skip_recent_month ?? true}
                                onChange={(checked) => setConfig((c) => ({
                                  ...c,
                                  rebalancing: { 
                                    ...c.rebalancing, 
                                    momentum_filter: { 
                                      ...c.rebalancing.momentum_filter,
                                      skip_recent_month: checked 
                                    }
                                  }
                                }))}
                              />
                              <span style={{ fontSize: 11, color: '#666' }}>
                                (12-1 momentum avoids reversal)
                              </span>
                            </div>
                          </Space>
                          <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                            ℹ️ Filters out stocks with negative price momentum to avoid "value traps". Classic 12-1 momentum skips the most recent month to avoid short-term reversal.
                          </div>
                        </div>
                      )}
                    </Space>
                  </div>
                  
                  {/* DAS-1689: Z-Score Baseline Configuration */}
                  <div style={{ marginTop: 16, padding: 12, background: '#f0f5ff', borderRadius: 8, border: '1px solid #adc6ff' }}>
                    <Typography.Text strong>📐 Z-Score Baseline</Typography.Text>
                    <div style={{ marginTop: 8 }}>
                      <Radio.Group
                        value={config.rebalancing.z_score_baseline || 'universe'}
                        onChange={(e) => setConfig((c) => ({
                          ...c,
                          rebalancing: { ...c.rebalancing, z_score_baseline: e.target.value }
                        }))}
                      >
                        <Space direction="vertical">
                          <Radio value="universe">
                            <span style={{ fontWeight: 500 }}>Universe</span>
                            <span style={{ color: '#666', marginLeft: 8 }}>— compare to filtered stocks</span>
                          </Radio>
                          <Radio value="market">
                            <span style={{ fontWeight: 500 }}>Market</span>
                            <span style={{ color: '#666', marginLeft: 8 }}>— compare to entire sector</span>
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 8, padding: 8, background: '#fff', borderRadius: 4 }}>
                      {(config.rebalancing.z_score_baseline || 'universe') === 'universe' ? (
                        <>ℹ️ <strong>Universe:</strong> Z-scores calculated on-the-fly from stocks in your filtered universe. Good for relative ranking within your selection.</>
                      ) : (
                        <>ℹ️ <strong>Market:</strong> Z-scores from pre-computed sector benchmarks. More aligned with academic factor investing - compares to entire market.</>
                      )}
                    </div>
                  </div>
                  
                  {/* DAS-1686: Factor Strategy Configuration */}
                  <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                    <Typography.Text strong>📊 Factor Strategy</Typography.Text>
                    <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                      <Space>
                        <div style={{ width: 200 }}>Strategy preset:</div>
                        <Select
                          style={{ width: 180 }}
                          value={config.rebalancing.factor_strategy?.preset || 'value'}
                          onChange={(val) => setConfig((c) => ({
                            ...c,
                            rebalancing: {
                              ...c.rebalancing,
                              factor_strategy: {
                                ...c.rebalancing.factor_strategy,
                                preset: val,
                                // Reset weights when switching to presets
                                ...(val === 'value' ? { roa_weight: 50, pb_weight: 50 } : {}),
                                ...(val === 'growth' ? { roa_weight: 100, pb_weight: 0 } : {})
                              }
                            }
                          }))}
                        >
                          <Select.Option value="value">Value</Select.Option>
                          <Select.Option value="growth">Growth</Select.Option>
                          <Select.Option value="custom">Custom</Select.Option>
                        </Select>
                      </Space>
                      
                      {/* Explanation based on preset */}
                      <div style={{ fontSize: 12, color: '#666', padding: 8, background: '#fff', borderRadius: 4, borderLeft: '3px solid #1890ff' }}>
                        {(config.rebalancing.factor_strategy?.preset || 'value') === 'value' && (
                          <>Selects profitable companies trading at low valuations. Favors high ROA (Return on Assets) combined with low P/B (Price-to-Book). Classic value investing approach.</>
                        )}
                        {config.rebalancing.factor_strategy?.preset === 'growth' && (
                          <>Selects the most profitable companies regardless of valuation. Only considers ROA - ignores P/B ratio. Allows high-growth stocks like tech leaders.</>
                        )}
                        {config.rebalancing.factor_strategy?.preset === 'custom' && (
                          <>Set your own factor weights. Adjust how much ROA and P/B influence stock selection.</>
                        )}
                      </div>
                      
                      {/* Custom sliders - only show when preset is 'custom' */}
                      {config.rebalancing.factor_strategy?.preset === 'custom' && (
                        <div style={{ padding: 12, background: '#fff', borderRadius: 4, border: '1px solid #d9d9d9' }}>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span>ROA Weight:</span>
                                <span style={{ fontWeight: 'bold' }}>{config.rebalancing.factor_strategy?.roa_weight || 50}%</span>
                              </div>
                              <Slider
                                min={0}
                                max={100}
                                step={5}
                                value={config.rebalancing.factor_strategy?.roa_weight || 50}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  rebalancing: {
                                    ...c.rebalancing,
                                    factor_strategy: { ...c.rebalancing.factor_strategy, roa_weight: val }
                                  }
                                }))}
                              />
                            </div>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span>P/B Weight:</span>
                                <span style={{ fontWeight: 'bold' }}>{config.rebalancing.factor_strategy?.pb_weight || 50}%</span>
                              </div>
                              <Slider
                                min={0}
                                max={100}
                                step={5}
                                value={config.rebalancing.factor_strategy?.pb_weight || 50}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  rebalancing: {
                                    ...c.rebalancing,
                                    factor_strategy: { ...c.rebalancing.factor_strategy, pb_weight: val }
                                  }
                                }))}
                              />
                            </div>
                          </Space>
                        </div>
                      )}
                      
                      {/* Formula display */}
                      <div style={{ fontFamily: 'monospace', fontSize: 12, padding: 8, background: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                        <strong>Formula: </strong>
                        {(() => {
                          const preset = config.rebalancing.factor_strategy?.preset || 'value';
                          const roaW = config.rebalancing.factor_strategy?.roa_weight || 50;
                          const pbW = config.rebalancing.factor_strategy?.pb_weight || 50;
                          if (preset === 'value') return 'score = 0.50 × ROA_zscore − 0.50 × PB_zscore';
                          if (preset === 'growth') return 'score = ROA_zscore';
                          // Custom
                          const roaFrac = (roaW / 100).toFixed(2);
                          const pbFrac = (pbW / 100).toFixed(2);
                          if (pbW === 0) return `score = ROA_zscore`;
                          return `score = ${roaFrac} × ROA_zscore − ${pbFrac} × PB_zscore`;
                        })()}
                      </div>
                    </Space>
                  </div>
                  
                  {/* DAS-1763: Regime-Based Factor Weight Adjustment */}
                  <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
                    <Space style={{ width: '100%' }} direction="vertical" size="small">
                      <Space>
                        <Typography.Text strong>📈 Regime Adjustment</Typography.Text>
                        <Switch
                          size="small"
                          checked={config.regime_adjustment?.enabled ?? false}
                          onChange={(checked) => setConfig((c) => ({
                            ...c,
                            regime_adjustment: { 
                              ...c.regime_adjustment,
                              enabled: checked 
                            }
                          }))}
                        />
                      </Space>
                      
                      {config.regime_adjustment?.enabled && (
                        <div style={{ marginTop: 8 }}>
                          <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 180, fontSize: 12 }}>Index for trend:</span>
                              <Select
                                size="small"
                                style={{ width: 160 }}
                                value={config.regime_adjustment?.index ?? 'sp500'}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  regime_adjustment: { 
                                    ...c.regime_adjustment,
                                    index: val 
                                  }
                                }))}
                              >
                                <Select.Option value="sp500">S&P 500</Select.Option>
                                <Select.Option value="msci_world">MSCI World</Select.Option>
                              </Select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 180, fontSize: 12 }}>VIX bear threshold:</span>
                              <InputNumber
                                size="small"
                                style={{ width: 80 }}
                                value={config.regime_adjustment?.vix_bear_threshold ?? 25}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  regime_adjustment: { 
                                    ...c.regime_adjustment,
                                    vix_bear_threshold: val ?? 25 
                                  }
                                }))}
                                step={1}
                                min={15}
                                max={50}
                              />
                              <span style={{ fontSize: 11, color: '#666' }}>
                                (VIX {'>'} this + below MA = BEAR)
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 180, fontSize: 12 }}>VIX bull threshold:</span>
                              <InputNumber
                                size="small"
                                style={{ width: 80 }}
                                value={config.regime_adjustment?.vix_bull_threshold ?? 20}
                                onChange={(val) => setConfig((c) => ({
                                  ...c,
                                  regime_adjustment: { 
                                    ...c.regime_adjustment,
                                    vix_bull_threshold: val ?? 20 
                                  }
                                }))}
                                step={1}
                                min={10}
                                max={35}
                              />
                              <span style={{ fontSize: 11, color: '#666' }}>
                                (VIX {'<'} this + above MA = BULL)
                              </span>
                            </div>
                            
                            <Divider style={{ margin: '8px 0' }} />
                            <Typography.Text style={{ fontSize: 12 }}>Factor weight multipliers:</Typography.Text>
                            
                            <div style={{ display: 'flex', gap: 24 }}>
                              {/* BEAR multipliers */}
                              <div style={{ flex: 1, padding: 8, background: '#fff1f0', borderRadius: 4 }}>
                                <div style={{ fontWeight: 500, marginBottom: 4, color: '#cf1322' }}>🐻 BEAR regime</div>
                                <Space direction="vertical" size={4}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                    <span style={{ width: 80 }}>ROA mult:</span>
                                    <InputNumber
                                      size="small"
                                      style={{ width: 70 }}
                                      value={config.regime_adjustment?.bear_roa_mult ?? 1.15}
                                      onChange={(val) => setConfig((c) => ({
                                        ...c,
                                        regime_adjustment: { 
                                          ...c.regime_adjustment,
                                          bear_roa_mult: val ?? 1.15 
                                        }
                                      }))}
                                      step={0.05}
                                      min={0.5}
                                      max={2.0}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                    <span style={{ width: 80 }}>P/B mult:</span>
                                    <InputNumber
                                      size="small"
                                      style={{ width: 70 }}
                                      value={config.regime_adjustment?.bear_pb_mult ?? 0.85}
                                      onChange={(val) => setConfig((c) => ({
                                        ...c,
                                        regime_adjustment: { 
                                          ...c.regime_adjustment,
                                          bear_pb_mult: val ?? 0.85 
                                        }
                                      }))}
                                      step={0.05}
                                      min={0}
                                      max={5.0}
                                    />
                                  </div>
                                </Space>
                              </div>
                              
                              {/* NEUTRAL multipliers */}
                              <div style={{ flex: 1, padding: 8, background: '#fffbe6', borderRadius: 4 }}>
                                <div style={{ fontWeight: 500, marginBottom: 4, color: '#d48806' }}>⚖️ NEUTRAL regime</div>
                                <Space direction="vertical" size={4}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                    <span style={{ width: 80 }}>ROA mult:</span>
                                    <InputNumber
                                      size="small"
                                      style={{ width: 70 }}
                                      value={config.regime_adjustment?.neutral_roa_mult ?? 0.9}
                                      onChange={(val) => setConfig((c) => ({
                                        ...c,
                                        regime_adjustment: { 
                                          ...c.regime_adjustment,
                                          neutral_roa_mult: val ?? 0.9 
                                        }
                                      }))}
                                      step={0.05}
                                      min={0.5}
                                      max={2.0}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                    <span style={{ width: 80 }}>P/B mult:</span>
                                    <InputNumber
                                      size="small"
                                      style={{ width: 70 }}
                                      value={config.regime_adjustment?.neutral_pb_mult ?? 2.0}
                                      onChange={(val) => setConfig((c) => ({
                                        ...c,
                                        regime_adjustment: { 
                                          ...c.regime_adjustment,
                                          neutral_pb_mult: val ?? 2.0 
                                        }
                                      }))}
                                      step={0.05}
                                      min={0}
                                      max={5.0}
                                    />
                                  </div>
                                </Space>
                              </div>
                              
                              {/* BULL multipliers */}
                              <div style={{ flex: 1, padding: 8, background: '#f6ffed', borderRadius: 4 }}>
                                <div style={{ fontWeight: 500, marginBottom: 4, color: '#389e0d' }}>🐂 BULL regime</div>
                                <Space direction="vertical" size={4}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                    <span style={{ width: 80 }}>ROA mult:</span>
                                    <InputNumber
                                      size="small"
                                      style={{ width: 70 }}
                                      value={config.regime_adjustment?.bull_roa_mult ?? 0.85}
                                      onChange={(val) => setConfig((c) => ({
                                        ...c,
                                        regime_adjustment: { 
                                          ...c.regime_adjustment,
                                          bull_roa_mult: val ?? 0.85 
                                        }
                                      }))}
                                      step={0.05}
                                      min={0.5}
                                      max={2.0}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                    <span style={{ width: 80 }}>P/B mult:</span>
                                    <InputNumber
                                      size="small"
                                      style={{ width: 70 }}
                                      value={config.regime_adjustment?.bull_pb_mult ?? 1.15}
                                      onChange={(val) => setConfig((c) => ({
                                        ...c,
                                        regime_adjustment: { 
                                          ...c.regime_adjustment,
                                          bull_pb_mult: val ?? 1.15 
                                        }
                                      }))}
                                      step={0.05}
                                      min={0}
                                      max={5.0}
                                    />
                                  </div>
                                </Space>
                              </div>
                            </div>
                          </Space>
                          {/* Position Sizing by Regime */}
                          <div style={{ marginTop: 12, padding: 8, background: '#f0f5ff', borderRadius: 4 }}>
                            <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8 }}>💰 Position Sizing by Regime</div>
                            <Space size="large">
                              <div>
                                <span style={{ fontSize: 11, color: '#666' }}>🐻 BEAR:</span>
                                <InputNumber
                                  size="small"
                                  style={{ width: 60, marginLeft: 4 }}
                                  value={config.regime_adjustment?.bear_position_mult ?? 0.7}
                                  onChange={(val) => setConfig((c) => ({
                                    ...c,
                                    regime_adjustment: { 
                                      ...c.regime_adjustment,
                                      bear_position_mult: val ?? 0.7 
                                    }
                                  }))}
                                  step={0.1}
                                  min={0.1}
                                  max={2.0}
                                />
                              </div>
                              <div>
                                <span style={{ fontSize: 11, color: '#666' }}>⚖️ NEUTRAL:</span>
                                <InputNumber
                                  size="small"
                                  style={{ width: 60, marginLeft: 4 }}
                                  value={config.regime_adjustment?.neutral_position_mult ?? 0.85}
                                  onChange={(val) => setConfig((c) => ({
                                    ...c,
                                    regime_adjustment: { 
                                      ...c.regime_adjustment,
                                      neutral_position_mult: val ?? 0.85 
                                    }
                                  }))}
                                  step={0.1}
                                  min={0.1}
                                  max={2.0}
                                />
                              </div>
                              <div>
                                <span style={{ fontSize: 11, color: '#666' }}>🐂 BULL:</span>
                                <InputNumber
                                  size="small"
                                  style={{ width: 60, marginLeft: 4 }}
                                  value={config.regime_adjustment?.bull_position_mult ?? 1.2}
                                  onChange={(val) => setConfig((c) => ({
                                    ...c,
                                    regime_adjustment: { 
                                      ...c.regime_adjustment,
                                      bull_position_mult: val ?? 1.2 
                                    }
                                  }))}
                                  step={0.1}
                                  min={0.5}
                                  max={3.0}
                                />
                              </div>
                            </Space>
                            <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                              Position size multiplier: BEAR=0.7→€14k, NEUTRAL=0.85→€17k, BULL=1.2→€24k (from €20k base)
                            </div>
                          </div>
                          
                          {/* Cash Reserve by Regime */}
                          <div style={{ marginTop: 12, padding: 8, background: '#fff7e6', borderRadius: 4 }}>
                            <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8 }}>💵 Cash Reserve by Regime</div>
                            <Space size="large">
                              <div>
                                <span style={{ fontSize: 11, color: '#666' }}>🐻 BEAR:</span>
                                <InputNumber
                                  size="small"
                                  style={{ width: 60, marginLeft: 4 }}
                                  value={config.regime_adjustment?.bear_cash_pct ?? 30}
                                  onChange={(val) => setConfig((c) => ({
                                    ...c,
                                    regime_adjustment: { 
                                      ...c.regime_adjustment,
                                      bear_cash_pct: val ?? 30 
                                    }
                                  }))}
                                  step={5}
                                  min={0}
                                  max={50}
                                  formatter={(v) => `${v}%`}
                                  parser={(v) => v.replace('%', '')}
                                />
                              </div>
                              <div>
                                <span style={{ fontSize: 11, color: '#666' }}>⚖️ NEUTRAL:</span>
                                <InputNumber
                                  size="small"
                                  style={{ width: 60, marginLeft: 4 }}
                                  value={config.regime_adjustment?.neutral_cash_pct ?? 20}
                                  onChange={(val) => setConfig((c) => ({
                                    ...c,
                                    regime_adjustment: { 
                                      ...c.regime_adjustment,
                                      neutral_cash_pct: val ?? 20 
                                    }
                                  }))}
                                  step={5}
                                  min={0}
                                  max={50}
                                  formatter={(v) => `${v}%`}
                                  parser={(v) => v.replace('%', '')}
                                />
                              </div>
                              <div>
                                <span style={{ fontSize: 11, color: '#666' }}>🐂 BULL:</span>
                                <InputNumber
                                  size="small"
                                  style={{ width: 60, marginLeft: 4 }}
                                  value={config.regime_adjustment?.bull_cash_pct ?? 0}
                                  onChange={(val) => setConfig((c) => ({
                                    ...c,
                                    regime_adjustment: { 
                                      ...c.regime_adjustment,
                                      bull_cash_pct: val ?? 0 
                                    }
                                  }))}
                                  step={5}
                                  min={0}
                                  max={50}
                                  formatter={(v) => `${v}%`}
                                  parser={(v) => v.replace('%', '')}
                                />
                              </div>
                            </Space>
                            <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                              Cash reserve %: BEAR=30% defensive, NEUTRAL=20% cautious, BULL=0% fully invested
                            </div>
                          </div>
                          
                          <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                            ℹ️ Adjusts factor weights, position sizes, and cash reserves based on market conditions. In BEAR markets, emphasizes value (P/B), reduces positions, and holds more cash. In BULL markets, allows growth and stays fully invested. Uses VIX + 200-day MA for detection.
                          </div>
                        </div>
                      )}
                    </Space>
                  </div>
                </>
              )}
              <div style={{ fontSize: 12, color: '#666' }}>
                <strong>Systematic rebalancing:</strong> Every N days, liquidate ALL positions and buy equal-weighted top-scoring stocks based on your factor strategy. Uses existing transaction cost config.
              </div>
            </Space>
          </div>
          <Divider dashed />
          {config.rebalancing.enabled && (
            <Typography.Text type="secondary">
              Signal-based scenarios are disabled while rebalancing is enabled. Toggle rebalancing off to configure alerts/clauses.
            </Typography.Text>
          )}
          {!config.rebalancing.enabled && (
            <>
              <Typography.Text strong>Signal source</Typography.Text>
              <div style={{ marginTop: 4, marginBottom: 8 }}>
                <Radio.Group
                  value={signalSource}
                  onChange={(e) => handleSignalSourceChange(e.target.value)}
                >
                  <Radio value="alerts">Use alerts</Radio>
                  <Radio value="clauses">Use clauses</Radio>
                  <Radio value="clause_set">Use clause combinations (AND)</Radio>
                </Radio.Group>
              </div>
              {signalSource === 'alerts' && (
                <>
                  <Typography.Text strong>Alert selection</Typography.Text>
                  <Radio.Group
                    value={config.alert_selection}
                    onChange={(e) => setConfig((c) => ({ ...c, alert_selection: e.target.value }))}
                  >
                    <Radio value="combined_accumulate">Combined accumulate</Radio>
                    <Radio value="individual">Individual</Radio>
                  </Radio.Group>
                  <div style={{ marginTop: 8 }}>
                    <Space>
                      <Select
                        allowClear
                        placeholder="Select Scenario"
                        style={{ width: 220 }}
                        value={config.scenario}
                        onChange={(val) =>
                          setConfig((c) => ({
                            ...c,
                            scenario: val || null,
                            alert_ids: val ? c.alert_ids : [],
                          }))
                        }
                        options={[{ label: 'Simple test 1 alert', value: 'Simple test 1 alert' }]}
                      />
                      <Button onClick={() => setConfig(c => ({ ...c, alertModalOpen: true }))} disabled={!config.scenario}>
                        Select Alerts
                      </Button>
                    </Space>
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#555' }}>
                      Selected alerts: {config.alert_ids?.length || 0}
                    </span>
                  </div>
                </>
              )}
              {signalSource === 'clauses' && (
                <div style={{ marginTop: 8 }}>
                  <Typography.Text strong>Clauses to evaluate</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Space>
                      <Select
                        mode="multiple"
                        style={{ minWidth: 320 }}
                        placeholder="Select one or more clauses"
                        loading={clauseDefinitionsLoading}
                        value={config.backtest_clause_ids}
                        onDropdownVisibleChange={(open) => {
                          if (open && clauseDefinitions.length === 0) {
                            loadClauseDefinitions();
                          }
                        }}
                        onChange={(vals) => setConfig((c) => ({ ...c, backtest_clause_ids: vals }))}
                        options={clauseDefinitions.map((cl) => ({
                          value: cl.id,
                          label: formatClauseLabel(cl),
                        }))}
                        allowClear
                      />
                      <Button
                        onClick={async () => {
                          await ensureClauseDataLoaded();
                          await ensureClauseSetsLoaded();
                          setClauseManagerTab('clauses');
                          setEditingClause(null);
                          setClauseForm({
                            id: null,
                            clause_field: '',
                            clause_field_sourcetable: '',
                            clause_operator: '<',
                            threshold_value: null,
                            threshold_field: '',
                            threshold_sourcetable: '',
                            clause_txt: '',
                            clause_scope: 'any',
                            trade_direction: 'buy',
                          });
                          setEditingClauseSet(null);
                          setClauseCombinationForm({
                            id: null,
                            name: '',
                            description: '',
                            clause_ids: [],
                          });
                          setClauseModalOpen(true);
                        }}
                      >
                        Manage clauses / combos
                      </Button>
                    </Space>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                      Selected clauses will drive the numeric backtesting engine for this configuration.
                    </div>
                  </div>
                </div>
              )}
              {signalSource === 'clause_set' && (
                <div style={{ marginTop: 8 }}>
                  <Typography.Text strong>Clause combination</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Space>
                      <Select
                        style={{ minWidth: 320 }}
                        placeholder="Select a clause combination"
                        value={config.backtest_clause_set_id}
                        loading={clauseSetsLoading}
                        onDropdownVisibleChange={(open) => {
                          if (open) {
                            loadClauseSets();
                          }
                        }}
                        onChange={(val) => setConfig((c) => ({ ...c, backtest_clause_set_id: val || null }))}
                        options={clauseSets.map((set) => ({
                          value: set.id,
                          label: `${set.name || `Combination #${set.id}`} (${set.clause_ids?.length || 0} clauses)`,
                        }))}
                        allowClear
                      />
                      <Button
                        onClick={async () => {
                          await ensureClauseDataLoaded();
                          await loadClauseSets();
                          setClauseManagerTab('combinations');
                          setEditingClause(null);
                          setClauseForm({
                            id: null,
                            clause_field: '',
                            clause_field_sourcetable: '',
                            clause_operator: '<',
                            threshold_value: null,
                            threshold_field: '',
                            threshold_sourcetable: '',
                            clause_txt: '',
                            clause_scope: 'any',
                            trade_direction: 'buy',
                          });
                          setEditingClauseSet(null);
                          setClauseCombinationForm({
                            id: null,
                            name: '',
                            description: '',
                            clause_ids: [],
                          });
                          setClauseModalOpen(true);
                        }}
                      >
                        Manage combinations
                      </Button>
                    </Space>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                      Selected combinations trigger trades only when all underlying clauses are true on the same day.
                    </div>
                  </div>
                </div>
              )}
              {signalSource !== 'alerts' && (
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>Clause cooldowns</Typography.Text>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Default applies to every clause or combination unless you define an override below.
                    <br />
                    <b>Days between trades</b> blocks the same clause from trading the same ticker within the window;{' '}
                    <b>Max trades per run</b> caps how many total trades a clause/combination can trigger.
                  </div>
                  <Space style={{ marginTop: 8 }} wrap>
                    <div style={{ minWidth: 220 }}>Default mode</div>
                    <Select
                      style={{ width: 220 }}
                      value={config.clause_cooldown_default.mode}
                      options={CLAUSE_COOLDOWN_MODE_OPTIONS}
                      onChange={(val) => updateClauseCooldownDefault({ mode: val })}
                    />
                    <InputNumber
                      min={1}
                      style={{ width: 160 }}
                      value={config.clause_cooldown_default.value}
                      onChange={(val) =>
                        updateClauseCooldownDefault({ value: Number(val || 1) })
                      }
                    />
                    <span>
                      {config.clause_cooldown_default.mode === 'count' ? 'trades per run' : 'days'}
                    </span>
                  </Space>
                  <div style={{ marginTop: 12 }}>
                    {[...selectedClauseRows, ...selectedClauseSetRows].length === 0 ? (
                      <Typography.Text type="secondary">
                        Select clauses or combinations above to configure specific overrides.
                      </Typography.Text>
                    ) : (
                      <>
                        {selectedClauseRows.map((entry) => renderClauseCooldownRow(entry))}
                        {selectedClauseSetRows.map((entry) => renderClauseCooldownRow(entry))}
                      </>
                    )}
                  </div>
                </div>
              )}
              {signalSource !== 'alerts' && (
                <div style={{ marginTop: 16 }}>
                  <Typography.Text strong>Clause position sizing overrides</Typography.Text>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Override buy/sell amounts for specific clauses or combinations. Leave blank to use defaults.
                  </div>
                  <div style={{ marginTop: 12 }}>
                    {[...selectedClauseRows, ...selectedClauseSetRows].length === 0 ? (
                      <Typography.Text type="secondary">
                        Select clauses or combinations above to configure specific overrides.
                      </Typography.Text>
                    ) : (
                      <>
                        {selectedClauseRows.map((entry) => renderClausePositionSizingRow(entry))}
                        {selectedClauseSetRows.map((entry) => renderClausePositionSizingRow(entry))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </Space>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Execution price model</Typography.Text>
        <Radio.Group
          value={config.execution_price_model}
          onChange={(e) => setConfig((c) => ({ ...c, execution_price_model: e.target.value }))}
        >
          <Radio value="open_next_day">Next-day open</Radio>
          <Radio value="close_same_day">Same-day close</Radio>
        </Radio.Group>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Position sizing</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Buy (new position, EUR):</div>
            <InputNumber
              min={0}
              step={100}
              value={config.position_sizing.buy.if_new_position.amount}
              onChange={(val) => setConfig((c) => ({ ...c, position_sizing: { ...c.position_sizing, buy: { ...c.position_sizing.buy, if_new_position: { ...c.position_sizing.buy.if_new_position, amount: Number(val || 0) } } } }))}
            />
          </Space>
          <Space>
            <div style={{ width: 220 }}>Buy (existing position, % equity):</div>
            <InputNumber
              min={0}
              max={100}
              step={0.5}
              value={config.position_sizing.buy.if_existing_position.percent}
              onChange={(val) => setConfig((c) => ({ ...c, position_sizing: { ...c.position_sizing, buy: { ...c.position_sizing.buy, if_existing_position: { ...c.position_sizing.buy.if_existing_position, percent: Number(val || 0) } } } }))}
            />
          </Space>
          <Space>
            <div style={{ width: 220 }}>Sell (% of position):</div>
            <InputNumber
              min={0}
              max={100}
              step={0.5}
              value={config.position_sizing.sell.percent}
              onChange={(val) => setConfig((c) => ({ ...c, position_sizing: { ...c.position_sizing, sell: { ...c.position_sizing.sell, percent: Number(val || 0) } } }))}
            />
          </Space>
        </Space>
      </div>
      <Divider />
      {/* DAS-1639: Only show cash reserve toggle if portfolio has cash */}
      {portfolioCashEur > 0 && (
        <div>
          <Typography.Text strong>Cash policy</Typography.Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <div style={{ width: 220 }}>Enable cash reserve:</div>
              <Switch
                checked={config.cash_policy.mode === 'keep_percent'}
                onChange={(checked) => setConfig((c) => ({ ...c, cash_policy: { ...c.cash_policy, mode: checked ? 'keep_percent' : 'none' } }))}
              />
            </Space>
            <Space>
              <div style={{ width: 220 }}>Keep cash (%):</div>
              <Tooltip title="Maintain a minimum cash reserve as a percentage of total equity. Prevents over-investing and ensures liquidity for new opportunities.">
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  value={config.cash_policy.target_pct}
                  onChange={(val) => setConfig((c) => ({ ...c, cash_policy: { ...c.cash_policy, target_pct: Number(val || 0) } }))}
                  disabled={config.cash_policy.mode !== 'keep_percent'}
                />
              </Tooltip>
            </Space>
            <div style={{ fontSize: 12, color: '#666' }}>
              When enabled, the engine maintains a minimum cash reserve (default 15%). Disable to allow full investment of available capital.
            </div>
          </Space>
        </div>
      )}
      <div>
        <Typography.Text strong>Run starting cash</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* DAS-1639: Show portfolio cash as read-only if available */}
          {portfolioCashEur > 0 ? (
            <Space>
              <div style={{ width: 220 }}>Portfolio cash (EUR):</div>
              <Typography.Text strong style={{ color: '#1890ff' }}>
                €{portfolioCashEur.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                (from portfolio definition)
              </Typography.Text>
            </Space>
          ) : (
            <Space>
              <div style={{ width: 220 }}>Start cash (EUR):</div>
              <InputNumber
                min={0}
                step={100}
                value={config.start_cash_eur}
                onChange={(val) => setConfig((c) => ({ ...c, start_cash_eur: Number(val || 0) }))}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                (no portfolio cash defined)
              </Typography.Text>
            </Space>
          )}
          <Space>
            <div style={{ width: 220 }}>Allow negative cash:</div>
            <Switch
              checked={config.allow_negative_cash}
              onChange={(checked) => setConfig((c) => ({ ...c, allow_negative_cash: !!checked }))}
            />
          </Space>
          <Space>
            <div style={{ width: 220 }}>Restrict alerts to overlap-only:</div>
            <Tooltip title="When enabled, only evaluate trading signals for tickers that have a price on the start date. This ensures consistency with equity valuation and prevents trading stocks added mid-run.">
              <Switch
                checked={config.restrict_alerts_to_overlap}
                onChange={(checked) => setConfig((c) => ({ ...c, restrict_alerts_to_overlap: !!checked }))}
              />
            </Tooltip>
          </Space>
        </Space>
        <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
          When ON: only tickers with a start-day price are evaluated for alerts (consistent with equity valuation). This is the recommended setting for accurate performance tracking.
        </div>
      </div>
      <div>
        <Tooltip title="Configure leverage limits and borrowing costs when cash goes negative (margin trading)">
          <Typography.Text strong>Risk & borrowing costs</Typography.Text>
        </Tooltip>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Max leverage (gross / equity):</div>
            <Tooltip title="Maximum ratio of total position value to equity. E.g., 2.0 allows positions worth 2x your equity.">
              <InputNumber
                min={1}
                max={10}
                step={0.1}
                value={config.risk.max_leverage_multiple}
                onChange={(val) => setConfig((c) => ({ ...c, risk: { ...c.risk, max_leverage_multiple: Number(val || 0) } }))}
              />
            </Tooltip>
          </Space>
          <Space>
            <div style={{ width: 220 }}>Max borrow (|cash| / start equity):</div>
            <Tooltip title="Maximum negative cash allowed as a multiple of starting equity. E.g., 0.5 allows borrowing up to 50% of initial equity.">
              <InputNumber
                min={0}
                max={5}
                step={0.1}
                value={config.risk.max_borrow_multiple}
                onChange={(val) => setConfig((c) => ({ ...c, risk: { ...c.risk, max_borrow_multiple: Number(val || 0) } }))}
              />
            </Tooltip>
          </Space>
          <Space>
            <div style={{ width: 220 }}>Borrow rate (annual %, EUR):</div>
            <Tooltip title="Annual interest rate charged on negative cash (borrowed funds). Applied daily to calculate borrowing costs.">
              <InputNumber
                min={0}
                max={50}
                step={0.25}
                value={config.risk.borrow_rate_annual_pct}
                onChange={(val) => setConfig((c) => ({ ...c, risk: { ...c.risk, borrow_rate_annual_pct: Number(val || 0) } }))}
              />
            </Tooltip>
          </Space>
          <Space>
            <div style={{ width: 220 }}>Maintenance margin (% equity):</div>
            <Tooltip title="Minimum equity percentage required to maintain positions. Falls below this trigger margin calls.">
              <InputNumber
                min={0}
                max={1}
                step={0.01}
                value={config.risk.maintenance_margin_pct}
                onChange={(val) => setConfig((c) => ({ ...c, risk: { ...c.risk, maintenance_margin_pct: Number(val || 0) } }))}
              />
            </Tooltip>
          </Space>
        </Space>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          Borrowing costs are calculated daily when cash is negative and reported in results. Use these settings to model realistic margin trading constraints.
        </div>
      </div>
      <div>
        <Typography.Text strong>Transaction costs</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 160 }}>Cost mode:</div>
            <Select
              style={{ width: 200 }}
              value={config.transaction_costs.mode || 'fixed'}
              onChange={(val) => setConfig((c) => ({ ...c, transaction_costs: { ...c.transaction_costs, mode: val } }))}
              options={[
                { value: 'fixed', label: 'Fixed amount (€)' },
                { value: 'bps', label: 'Percentage (bps)' },
              ]}
            />
          </Space>
          {config.transaction_costs.mode === 'fixed' ? (
            <Space>
              <div style={{ width: 160 }}>Buy (€):</div>
              <InputNumber
                min={0}
                step={0.1}
                value={config.transaction_costs.buy_fixed_eur}
                onChange={(val) => setConfig((c) => ({ ...c, transaction_costs: { ...c.transaction_costs, buy_fixed_eur: Number(val || 2.0) } }))}
              />
              <div style={{ width: 80, textAlign: 'right' }}>Sell (€):</div>
              <InputNumber
                min={0}
                step={0.1}
                value={config.transaction_costs.sell_fixed_eur}
                onChange={(val) => setConfig((c) => ({ ...c, transaction_costs: { ...c.transaction_costs, sell_fixed_eur: Number(val || 2.0) } }))}
              />
            </Space>
          ) : (
            <Space>
              <div style={{ width: 160 }}>Buy (bps):</div>
              <InputNumber
                min={0}
                step={1}
                value={config.transaction_costs.buy_bps}
                onChange={(val) => setConfig((c) => ({ ...c, transaction_costs: { ...c.transaction_costs, buy_bps: Number(val || 0) } }))}
              />
              <div style={{ width: 80, textAlign: 'right' }}>Sell (bps):</div>
              <InputNumber
                min={0}
                step={1}
                value={config.transaction_costs.sell_bps}
                onChange={(val) => setConfig((c) => ({ ...c, transaction_costs: { ...c.transaction_costs, sell_bps: Number(val || 0) } }))}
              />
            </Space>
          )}
        </Space>
      </div>
      
      {/* DAS-1773: Slippage Modeling */}
      <div style={{ marginTop: 12, padding: 12, background: '#fff1f0', borderRadius: 4 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Space>
            <Typography.Text strong>📊 Slippage Modeling</Typography.Text>
            <Switch
              size="small"
              checked={!!config.slippage?.enabled}
              onChange={(checked) => setConfig((c) => ({
                ...c,
                slippage: { ...c.slippage, enabled: checked }
              }))}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Models bid-ask spread & market impact by market cap
            </Typography.Text>
          </Space>
          {config.slippage?.enabled && (
            <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 4 }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space>
                  <div style={{ width: 160 }}>🏛️ Large Cap (&gt;€10B):</div>
                  <InputNumber
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={2}
                    step={0.05}
                    value={config.slippage?.large_cap_pct ?? 0.02}
                    onChange={(val) => setConfig((c) => ({
                      ...c,
                      slippage: { ...c.slippage, large_cap_pct: val ?? 0.02 }
                    }))}
                  />
                  <span style={{ fontSize: 11, color: '#666' }}>%</span>
                </Space>
                <Space>
                  <div style={{ width: 160 }}>🏢 Mid Cap (€2-10B):</div>
                  <InputNumber
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={2}
                    step={0.05}
                    value={config.slippage?.mid_cap_pct ?? 0.08}
                    onChange={(val) => setConfig((c) => ({
                      ...c,
                      slippage: { ...c.slippage, mid_cap_pct: val ?? 0.08 }
                    }))}
                  />
                  <span style={{ fontSize: 11, color: '#666' }}>%</span>
                </Space>
                <Space>
                  <div style={{ width: 160 }}>🏠 Small Cap (&lt;€2B):</div>
                  <InputNumber
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={2}
                    step={0.05}
                    value={config.slippage?.small_cap_pct ?? 0.35}
                    onChange={(val) => setConfig((c) => ({
                      ...c,
                      slippage: { ...c.slippage, small_cap_pct: val ?? 0.35 }
                    }))}
                  />
                  <span style={{ fontSize: 11, color: '#666' }}>%</span>
                </Space>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Slippage simulates bid-ask spread and market impact. Applied to execution price: 
                  BUY = price × (1 + slip%), SELL = price × (1 - slip%)
                </div>
              </Space>
            </div>
          )}
        </Space>
      </div>
      
      <div>
        <Typography.Text strong>Dividends</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Dividend modelling:</div>
            <Select
              style={{ width: 220 }}
              value={config.dividends.schedule}
              onChange={(val) =>
                setConfig((c) => ({
                  ...c,
                  dividends: { ...c.dividends, schedule: val },
                }))
              }
              options={[
                { label: 'Enabled (regular cash dividends)', value: 'quarterly' },
                { label: 'Disabled (ignore dividends)', value: 'none' },
              ]}
            />
          </Space>
          <Space>
            <div style={{ width: 220 }}>Include special dividends (SC):</div>
            <Tooltip title="Include one-time special dividends (marked as 'SC' type) in addition to regular quarterly dividends. Special dividends are typically large, non-recurring payouts.">
              <Switch
                checked={!!config.dividends.include_special}
                onChange={(checked) =>
                  setConfig((c) => ({
                    ...c,
                    dividends: { ...c.dividends, include_special: checked },
                  }))
                }
                disabled={config.dividends.schedule === 'none'}
              />
            </Tooltip>
          </Space>
          <div style={{ fontSize: 12, color: '#666' }}>
            When enabled, cash dividends are credited on the ex‑dividend date for any holdings in the
            start portfolio. Special dividends are optional and modelled only when explicitly enabled.
          </div>
        </Space>
      </div>
      <div>
        <Typography.Text strong>Splits</Typography.Text>
        <Space>
          <div style={{ width: 200 }}>Price ratio threshold:</div>
          <Tooltip title="Minimum price ratio (day-over-day) to flag as a potential stock split. E.g., 1.5 means a 50%+ price jump triggers a split warning. Used to detect data anomalies.">
            <InputNumber
              min={1}
              step={0.1}
              value={config.splits.price_ratio_threshold}
              onChange={(val) => setConfig((c) => ({ ...c, splits: { ...c.splits, price_ratio_threshold: Number(val || 0) } }))}
            />
          </Tooltip>
        </Space>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          Stock splits are automatically detected and reported in results. This threshold helps identify unusual price movements that may indicate splits or data issues.
        </div>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Liquidity filters</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Enable liquidity filters:</div>
            <Switch
              checked={config.liquidity_filters.enabled}
              onChange={(checked) => setConfig((c) => ({ ...c, liquidity_filters: { ...c.liquidity_filters, enabled: checked } }))}
            />
          </Space>
          {config.liquidity_filters.enabled && (
            <>
              <Space>
                <div style={{ width: 220 }}>Min Market Cap (€B):</div>
                <InputNumber
                  min={0}
                  step={1}
                  placeholder="e.g. 50"
                  value={config.liquidity_filters.min_market_cap_eur ? config.liquidity_filters.min_market_cap_eur / 1000000000 : null}
                  onChange={(val) => setConfig((c) => ({ ...c, liquidity_filters: { ...c.liquidity_filters, min_market_cap_eur: val ? val * 1000000000 : null } }))}
                  style={{ width: 100 }}
                />
              </Space>
              <Space>
                <div style={{ width: 220 }}>Min Avg Daily $ Volume:</div>
                <InputNumber
                  min={0}
                  step={100000}
                  value={config.liquidity_filters.min_avg_daily_dollar_volume}
                  onChange={(val) => setConfig((c) => ({ ...c, liquidity_filters: { ...c.liquidity_filters, min_avg_daily_dollar_volume: val ?? null } }))}
                />
              </Space>
            </>
          )}
          <div style={{ fontSize: 12, color: '#666' }}>
            When enabled, only stocks meeting the minimum market cap and volume thresholds will be considered for trading signals. Leave disabled to evaluate all stocks.
          </div>
        </Space>
      </div>
      <Divider />
      <div>
        {/* DAS-1778: Stock Region Filter - simple US/Non-US selection */}
        <Typography.Text strong>Stock Region Filter</Typography.Text>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Space>
            <div style={{ width: 220 }}>Include stocks from:</div>
            <Select
              style={{ width: 200 }}
              value={config.currency_filters?.stock_region || 'all'}
              onChange={(val) => setConfig((c) => ({ 
                ...c, 
                currency_filters: { 
                  ...c.currency_filters, 
                  stock_region: val 
                } 
              }))}
              options={[
                { label: '🌍 All stocks', value: 'all' },
                { label: '🇺🇸 US only (no suffix)', value: 'us_only' },
                { label: '🇪🇺 Non-US only (with suffix)', value: 'non_us_only' },
              ]}
            />
          </Space>
          <div style={{ fontSize: 11, color: '#888', marginLeft: 220 }}>
            US stocks have no suffix (e.g., AAPL). Non-US have suffixes like .L, .DE, .PA
          </div>
        </Space>

        {/* DAS-1741: Currency/Exchange Filter Section */}
        <Typography.Text strong>Currency / Exchange restrictions (advanced)</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Enable advanced filters:</div>
            <Switch
              checked={config.currency_filters?.enabled || false}
              onChange={(checked) => setConfig((c) => ({ 
                ...c, 
                currency_filters: { 
                  ...c.currency_filters, 
                  enabled: checked 
                } 
              }))}
            />
          </Space>
          {config.currency_filters?.enabled && (
            <>
              <Space align="start">
                <div style={{ width: 220 }}>Allowed currencies:</div>
                <div style={{ maxWidth: 400 }}>
                  <Select
                    mode="multiple"
                    style={{ width: 300 }}
                    placeholder="All currencies (no restriction)"
                    value={config.currency_filters?.allowed_currencies || []}
                    onChange={(vals) => setConfig((c) => ({ 
                      ...c, 
                      currency_filters: { 
                        ...c.currency_filters, 
                        allowed_currencies: vals.length > 0 ? vals : null 
                      } 
                    }))}
                    options={[
                      { label: 'USD (US Dollar)', value: 'USD' },
                      { label: 'EUR (Euro)', value: 'EUR' },
                      { label: 'GBP (British Pound)', value: 'GBP' },
                      { label: 'JPY (Japanese Yen)', value: 'JPY' },
                      { label: 'HKD (Hong Kong Dollar)', value: 'HKD' },
                      { label: 'TWD (Taiwan Dollar)', value: 'TWD' },
                      { label: 'CHF (Swiss Franc)', value: 'CHF' },
                      { label: 'CAD (Canadian Dollar)', value: 'CAD' },
                      { label: 'AUD (Australian Dollar)', value: 'AUD' },
                    ]}
                    allowClear
                  />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    Leave empty to allow all currencies
                  </div>
                </div>
              </Space>
              <Space align="start">
                <div style={{ width: 220 }}>Excluded exchanges (suffixes):</div>
                <div style={{ maxWidth: 400 }}>
                  <Select
                    mode="multiple"
                    style={{ width: 300 }}
                    placeholder="None excluded"
                    value={config.currency_filters?.excluded_exchanges || []}
                    onChange={(vals) => setConfig((c) => ({ 
                      ...c, 
                      currency_filters: { 
                        ...c.currency_filters, 
                        excluded_exchanges: vals 
                      } 
                    }))}
                    options={[
                      { label: '.T (Tokyo)', value: '.T' },
                      { label: '.HK (Hong Kong)', value: '.HK' },
                      { label: '.TW (Taiwan)', value: '.TW' },
                      { label: '.L (London)', value: '.L' },
                      { label: '.AS (Amsterdam)', value: '.AS' },
                      { label: '.DE (Germany)', value: '.DE' },
                      { label: '.PA (Paris)', value: '.PA' },
                      { label: '.MI (Milan)', value: '.MI' },
                      { label: '.SW (Swiss)', value: '.SW' },
                    ]}
                    allowClear
                  />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    Exclude stocks by exchange suffix (e.g., .T for Tokyo)
                  </div>
                </div>
              </Space>
            </>
          )}
          <div style={{ fontSize: 12, color: '#666' }}>
            When enabled, rebalancing will only consider stocks matching the currency/exchange criteria. Useful for limiting to USD-only stocks or excluding markets with incomplete FX data.
          </div>
        </Space>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Benchmark strategy</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Primary benchmark:</div>
            <div style={{ fontWeight: 500 }}>Buy-and-Hold of Start Portfolio</div>
          </Space>
          <Space>
            <div style={{ width: 220 }}>Secondary benchmark (optional):</div>
            <Select
              style={{ width: 300 }}
              value={config.benchmark.secondary}
              onChange={(val) => setConfig((c) => ({ ...c, benchmark: { ...c.benchmark, secondary: val } }))}
              allowClear
              placeholder="Select a secondary benchmark"
              options={[
                { label: 'MSCI World (EUR)', value: 'MSCI World (EUR)' },
                { label: 'S&P 500 (EUR)', value: 'S&P 500 (EUR)' },
                { label: 'Nasdaq', value: 'Nasdaq' },
                { label: 'Dow Jones Industrial (DJI)', value: 'Dow Jones Industrial (DJI)' },
                { label: 'Russell 2000', value: 'Russell 2000' },
                { label: 'Gold', value: 'Gold' },
                { label: 'Your existing portfolio performance', value: 'Your existing portfolio performance' },
              ]}
            />
          </Space>
          <div style={{ fontSize: 12, color: '#666' }}>
            The primary benchmark (Buy-and-Hold) measures your strategy against holding the start portfolio unchanged. The secondary benchmark compares against a market index.
          </div>
        </Space>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Survivorship bias</Typography.Text>
        <Space>
          <div style={{ width: 220 }}>Avoid survivorship bias:</div>
          <Switch
            checked={config.survivorship_bias.avoid_survivorship_bias}
            onChange={(checked) => setConfig((c) => ({ ...c, survivorship_bias: { ...c.survivorship_bias, avoid_survivorship_bias: checked } }))}
          />
        </Space>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          When enabled, includes delisted/failed companies in the trading universe. This prevents overly optimistic results by accounting for stocks that didn't survive the test period.
        </div>
      </div>
      <div>
        <Typography.Text strong>Engine</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 220 }}>Per-day sample limit (0 = unlimited):</div>
            <InputNumber
              min={0}
              step={50}
              value={config.engine.sample_limit}
              onChange={(val) => setConfig((c) => ({ ...c, engine: { ...c.engine, sample_limit: Number(val || 0) } }))}
            />
          </Space>
          <Space>
            <div style={{ width: 220 }}>Alert cooldown (days, default):</div>
            <Tooltip title="LEGACY: Minimum days between alerts for the same ticker (global default). This is now superseded by per-clause and per-combination cooldown settings. Set to 0 to rely only on clause-specific cooldowns.">
              <InputNumber
                min={0}
                step={1}
                value={config.engine.cooldown_days_default}
                onChange={(val) => setConfig((c) => ({ ...c, engine: { ...c.engine, cooldown_days_default: Number(val || 0) } }))}
              />
            </Tooltip>
          </Space>
          <div style={{ fontSize: 12, color: '#666' }}>
            Valuation coverage: <b>Overlap-only</b> (exclude tickers missing start-day price).
          </div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            <Tooltip title="'Overlap-only' means only tickers with both a start-day and end-day price are included in equity valuation. This ensures consistent performance calculations by excluding tickers added mid-run.">
              <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>ℹ️ What is "Overlap-only"?</span>
            </Tooltip> Only tickers with prices at both start and end dates are included in equity calculations. This ensures accurate performance tracking.
          </div>
        </Space>
      </div>
      <div>
        <Typography.Text strong>Data gaps handling</Typography.Text>
        <Radio.Group
          value={config.data_gaps.mode}
          onChange={(e) => setConfig((c) => ({ ...c, data_gaps: { ...c.data_gaps, mode: e.target.value } }))}
        >
          <Radio value="skip">Skip</Radio>
          <Radio value="abort">Abort</Radio>
        </Radio.Group>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Constraints (optional)</Typography.Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <div style={{ width: 240 }}>Enable max positions:</div>
            <Switch
              checked={config.constraints.max_positions != null}
              onChange={(checked) => setConfig((c) => ({ ...c, constraints: { ...c.constraints, max_positions: checked ? 10 : null } }))}
            />
          </Space>
          {config.constraints.max_positions != null && (
            <Space>
              <div style={{ width: 240 }}>Max positions:</div>
              <InputNumber
                min={1}
                value={config.constraints.max_positions}
                onChange={(val) => setConfig((c) => ({ ...c, constraints: { ...c.constraints, max_positions: val ?? null } }))}
              />
            </Space>
          )}
          <Space>
            <div style={{ width: 240 }}>Enable max exposure per ticker:</div>
            <Switch
              checked={config.constraints.max_exposure_per_ticker_pct != null}
              onChange={(checked) => setConfig((c) => ({ ...c, constraints: { ...c.constraints, max_exposure_per_ticker_pct: checked ? 10 : null } }))}
            />
          </Space>
          {config.constraints.max_exposure_per_ticker_pct != null && (
            <Space>
              <div style={{ width: 240 }}>Max exposure per ticker (%):</div>
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                value={config.constraints.max_exposure_per_ticker_pct}
                onChange={(val) => setConfig((c) => ({ ...c, constraints: { ...c.constraints, max_exposure_per_ticker_pct: val ?? null } }))}
              />
            </Space>
          )}
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Optional portfolio constraints to limit concentration risk and enforce diversification. Leave disabled for no limits.
          </div>
        </Space>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Base currency</Typography.Text>
        <div>EUR (fixed)</div>
      </div>
      <Divider />
      <div>
        <Typography.Text strong>Preview (read-only)</Typography.Text>
        <Input.TextArea
          rows={14}
          readOnly
          value={JSON.stringify({ ...config, start_portfolio_id: selected?.id || config.start_portfolio_id }, null, 2)}
        />
      </div>
    </Space>
  );
};

export default ConfigurationForm;

